import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { ButtonComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { environment } from '../../../../../../environments/environment';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

declare let google: {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        ux_mode: string;
        callback: (response: { credential: string }) => void;
      }) => void;
      prompt: () => void;
      renderButton: (parent: HTMLElement, options: { type: string }) => void;
    };
  };
};

@Component({
  selector: 'app-platform-google-signin',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  template: `
    <app-button
      class="google-button"
      variant="white-grey"
      [loading]="loading() ?? false"
      [disableOnNoConnection]="true"
      (click)="handleGoogleSignIn()"
      [fullWidth]="true"
    >
      <svg class="google-icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
      </svg>
      {{ label() }}
    </app-button>
  `,
  styleUrl: './platform-google-signin.component.scss',
})
export class PlatformGoogleSigninComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly auth = inject(PlatformAuthService);
  private readonly toastsService = inject(ToastsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly clientId = environment.googleClientId;

  readonly label = input<string>('');
  readonly loading = input<boolean>();
  readonly isRegistration = input(false);
  readonly redirectUrl = input<string | null>(null);
  readonly termsAccepted = input(false);
  readonly talenthubAccepted = input(false);

  onLoading = output<boolean>();
  loggedIn = output<void>();

  private googleLoginWrapper: { click: () => void } | null = null;

  ngOnInit(): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.initialize({
        client_id: this.clientId,
        ux_mode: 'popup',
        callback: (response) => this.handleCredentialResponse(response),
      });
      this.googleLoginWrapper = this.createGoogleLoginWrapper();
    }
  }

  ngAfterViewInit(): void {
    if (typeof google !== 'undefined') {
      google.accounts.id.prompt();
    }
  }

  /**
   * Opens the Google Identity Services popup.
   */
  handleGoogleSignIn(): void {
    if (this.isRegistration() && !this.termsAccepted()) {
      this.toastsService.warning('jobboard.platform.auth.register.terms-required');
      return;
    }
    if (typeof google !== 'undefined' && this.googleLoginWrapper) {
      this.googleLoginWrapper.click();
    }
  }

  /**
   * Completes Google sign-in with the ID token.
   * @param response - GIS credential response
   */
  private handleCredentialResponse(response: { credential: string }): void {
    this.onLoading.emit(true);
    this.auth
      .signInWithGoogle({
        token: response.credential,
        rememberMe: true,
        termsAccepted: this.isRegistration() ? this.termsAccepted() : undefined,
        talenthubAccepted: this.isRegistration() ? this.talenthubAccepted() : undefined,
      })
      .subscribe({
        next: () => {
          this.loggedIn.emit();
          this.navigateAfterLogin();
          this.onLoading.emit(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastsService.warning('jobboard.platform.auth.login.invalid-credentials');
          this.onLoading.emit(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Navigates to a redirect target or the dashboard.
   */
  private navigateAfterLogin(): void {
    const redirectUrl = this.redirectUrl();
    if (redirectUrl && redirectUrl !== this.router.url) {
      this.router.navigateByUrl(redirectUrl);
      return;
    }
    if (!redirectUrl) {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Creates a hidden GIS button that can be clicked from the visible UI button.
   * @returns Click wrapper
   */
  private createGoogleLoginWrapper(): { click: () => void } {
    const googleLoginWrapper = document.createElement('div');
    googleLoginWrapper.style.display = 'none';
    document.body.appendChild(googleLoginWrapper);
    google.accounts.id.renderButton(googleLoginWrapper, {
      type: 'icon',
    });
    const googleLoginWrapperButton = googleLoginWrapper.querySelector(
      'div[role=button]'
    ) as HTMLElement | null;
    return {
      click: () => googleLoginWrapperButton?.click(),
    };
  }
}
